<script setup lang="ts">
import { ref } from 'vue'
import { useDisplay } from 'vuetify'
import FolderTree from '../FolderTree.vue'
import type { FolderItem } from '@/modules/documents-new/types/folder.types'
import { pluralize } from '@/modules/documents-new/utils/pluralize'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'

interface Props {
  folders: FolderItem[]
  documentsCount: number
  title?: string
}

interface Emits {
  (e: 'move', folderId: string): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Переместить документы',
})
const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const isOpen = ref(false)

const open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
  emit('cancel')
}

const handleSelect = (folderId: string | null) => {
  if (folderId == null || folderId === '') {
    return
  }

  emit('move', folderId)
  isOpen.value = false
}

const getMoveText = () => {
  const count = props.documentsCount
  if (count === 1) {
    return 'Выберите папку для перемещения'
  }
  const word = pluralize(count, 'элемент', 'элемента', 'элементов')
  return `Выберите папку куда перенести ${count} ${word}`
}

defineExpose({ open, close })
</script>

<template>
  <VDialog
    v-model="isOpen"
    :max-width="smAndDown ? '100%' : 500"
    :fullscreen="smAndDown"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-6">
        <span class="text-h4">{{ title }}</span>
        <CustomCloseButton :close-action="close" />
      </div>

      <div class="text-body-1 mb-4">
        {{ getMoveText() }}
      </div>

      <div style="max-height: 500px; overflow-y: auto;">
        <FolderTree
          :folders="folders"
          :select-on-arrow="false"
          @select="handleSelect"
        />
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.v-card) {
  border-radius: 12px;
}
</style>
